import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { UserChipPicker } from '../components/ui/UserChipPicker';
import { ErrorState } from '../components/feedback/ErrorState';
import { useAuth } from '../hooks/useAuth.js';
import {
  usePipelinePermissions,
  useSavePipelinePermissions,
} from '../hooks/usePipelinePermissions.js';
import type { FeatureOperators, PipelineFeatureId, TargetDbOperators } from '../types/api.js';

/** @graph-connects none */
const FEATURE_IDS: PipelineFeatureId[] = ['database', 'mongodb', 'jobcan', 'spreadsheet', 'drive'];

/** 種別ごとの見出しの翻訳キー。 @graph-connects none */
const FEATURE_LABEL_KEYS: Record<PipelineFeatureId, string> = {
  database: 'pipelinePermissions.features.database',
  mongodb: 'pipelinePermissions.features.mongodb',
  jobcan: 'pipelinePermissions.features.jobcan',
  spreadsheet: 'pipelinePermissions.features.spreadsheet',
  drive: 'pipelinePermissions.features.drive',
};

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business pipeline 管理権限（admins / featureOperators / targetDbOperators）の編集ページ。pipeline_permissions.admins のみアクセス可能。UserChipPicker でユーザーを workspaceId 単位で選択
 * @graph-connects useAuth [calls] 現在のユーザー情報取得（isPipelineAdmin チェック）
 * @graph-connects usePipelinePermissions [calls] 現状の pipeline_permissions 取得
 * @graph-connects useSavePipelinePermissions [calls] 保存ミューテーション
 * @graph-connects useToast [calls] 結果通知
 * @graph-connects UserChipPicker [calls] 検索+chip 形式の多選択ピッカー
 */
export const Route = createFileRoute('/pipeline-permissions')({
  component: PipelinePermissionsPage,
});

/** @graph-connects none */
export function PipelinePermissionsPage() {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data, isLoading, isError, refetch } = usePipelinePermissions();
  const save = useSavePipelinePermissions();
  const { showToast } = useToast();

  // 編集用ローカル state (内部値は Google Workspace user id 配列)
  const [admins, setAdmins] = useState<string[]>([]);
  const [featureOps, setFeatureOps] = useState<Record<PipelineFeatureId, string[]>>({
    database: [],
    mongodb: [],
    jobcan: [],
    spreadsheet: [],
    drive: [],
  });
  const [targetDbs, setTargetDbs] = useState<{ db: string; users: string[] }[]>([]);

  // サーバ値で初期化
  useEffect(() => {
    if (!data) return;
    setAdmins([...data.admins]);
    setFeatureOps({
      database: [...(data.featureOperators.database ?? [])],
      mongodb: [...(data.featureOperators.mongodb ?? [])],
      jobcan: [...(data.featureOperators.jobcan ?? [])],
      spreadsheet: [...(data.featureOperators.spreadsheet ?? [])],
      drive: [...(data.featureOperators.drive ?? [])],
    });
    setTargetDbs(
      Object.entries(data.targetDbOperators ?? {}).map(([db, users]) => ({
        db,
        users: [...users],
      })),
    );
  }, [data]);

  // 整形のみの派生値。Rules of Hooks 違反を避けるため early return より前で算出する
  const lastUpdatedLabel = data
    ? t('pipelinePermissions.lastUpdated', {
        by: data.updatedBy,
        at: data.updatedAt ? new Date(data.updatedAt).toLocaleString() : t('common.unknown'),
      })
    : '';

  if (isAuthLoading) {
    return <Skeleton className="h-32" />;
  }
  if (user && !user.isPipelineAdmin) {
    return (
      <ErrorState
        title={t('common.accessDeniedTitle')}
        message={t('pipelinePermissions.accessDenied')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title={t('common.loadFailedTitle')}
        message={t('pipelinePermissions.loadFailed')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const handleSave = (): void => {
    const payload = {
      admins,
      featureOperators: FEATURE_IDS.reduce<FeatureOperators>((acc, feature) => {
        const list = featureOps[feature];
        if (list.length > 0) acc[feature] = list;
        return acc;
      }, {}),
      targetDbOperators: targetDbs.reduce<TargetDbOperators>((acc, row) => {
        const db = row.db.trim();
        if (db.length > 0 && row.users.length > 0) acc[db] = row.users;
        return acc;
      }, {}),
    };

    save.mutate(payload, {
      onSuccess: () => {
        showToast('success', t('pipelinePermissions.saveSuccess'));
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : String(error);
        showToast('error', t('pipelinePermissions.saveError', { reason: message }));
      },
    });
  };

  const isSaving = save.isPending;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('pipelinePermissions.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('pipelinePermissions.description')}
        </p>
      </header>

      {/* admins */}
      <section className="glass-card p-6 space-y-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('pipelinePermissions.adminsTitle')}
        </h2>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('pipelinePermissions.adminsDescription')}
        </p>
        <UserChipPicker
          value={admins}
          onChange={setAdmins}
          ariaLabel="admins"
          placeholder={t('pipelinePermissions.searchPlaceholder')}
        />
      </section>

      {/* featureOperators */}
      <section className="glass-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('pipelinePermissions.featureOperatorsTitle')}
        </h2>
        <p className="text-xs text-[var(--color-text-secondary)]">
          {t('pipelinePermissions.featureOperatorsDescription')}
        </p>
        {FEATURE_IDS.map((feature) => (
          <div key={feature} className="space-y-2">
            <label className="text-xs font-medium text-[var(--color-text-secondary)]">
              {t(FEATURE_LABEL_KEYS[feature])}
            </label>
            <UserChipPicker
              value={featureOps[feature]}
              onChange={(next) => {
                setFeatureOps((prev) => ({ ...prev, [feature]: next }));
              }}
              ariaLabel={`${feature} operators`}
              placeholder={t('pipelinePermissions.searchPlaceholder')}
            />
          </div>
        ))}
      </section>

      {/* targetDbOperators */}
      <section className="glass-card p-6 space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('pipelinePermissions.targetDbOperatorsTitle')}
            </h2>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">
              {t('pipelinePermissions.targetDbOperatorsDescription')}
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setTargetDbs((prev) => [...prev, { db: '', users: [] }]);
            }}
          >
            {t('pipelinePermissions.addRow')}
          </Button>
        </header>
        {targetDbs.length === 0 && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('pipelinePermissions.targetDbEmpty')}
          </p>
        )}
        {targetDbs.map((row, idx) => (
          <div key={idx} className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                aria-label={`targetDb name #${String(idx + 1)}`}
                type="text"
                placeholder={t('pipelinePermissions.dbNamePlaceholder')}
                className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm font-mono text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-focus)]"
                value={row.db}
                onChange={(e) => {
                  const value = e.target.value;
                  setTargetDbs((prev) => prev.map((r, i) => (i === idx ? { ...r, db: value } : r)));
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`remove targetDb #${String(idx + 1)}`}
                onClick={() => {
                  setTargetDbs((prev) => prev.filter((_, i) => i !== idx));
                }}
              >
                {t('common.delete')}
              </Button>
            </div>
            <UserChipPicker
              value={row.users}
              onChange={(next) => {
                setTargetDbs((prev) => prev.map((r, i) => (i === idx ? { ...r, users: next } : r)));
              }}
              ariaLabel={`targetDb ${row.db} operators`}
              placeholder={t('pipelinePermissions.searchPlaceholder')}
            />
          </div>
        ))}
      </section>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[var(--color-text-secondary)]">{lastUpdatedLabel}</p>
        <Button type="button" onClick={handleSave} loading={isSaving} disabled={isSaving}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
